import { useState, useCallback, useMemo } from 'react';
import FlexService from '../../services/FlexService';
import actionHandlerRegistry from './registry/actionHandlerRegistry';
import { ENTITY_FIELDS_CHANGED } from './AddCustomFieldDialog';
import { savedMessage } from './workspaceUtils';

/**
 * Hook for workspace action execution and overlay state management.
 */
const useWorkspaceActions = ({ activeView, resolvedDetails, loadMasterData, onError, onSuccess, pendingNavParamsRef, setActiveViewId, navParams, onOpenWorkspace }) => {
  // Edit/View overlay state
  const [overlayMode, setOverlayMode] = useState(null);
  const [overlayEntity, setOverlayEntity] = useState(null);
  const [overlayRowData, setOverlayRowData] = useState(null);
  const [overlayAction, setOverlayAction] = useState(null);

  // Form overlay state
  const [formOpen, setFormOpen] = useState(false);
  const [formEntity, setFormEntity] = useState(null);
  const [formDetailEntity, setFormDetailEntity] = useState(null);
  const [formKey, setFormKey] = useState(null);
  const [formRowData, setFormRowData] = useState(null);
  const [formScriptName, setFormScriptName] = useState(null);
  const [formTitle, setFormTitle] = useState(null);
  const [formSaveLabel, setFormSaveLabel] = useState(null);
  const [formForceCreate, setFormForceCreate] = useState(false);

  // Rapid-entry overlay state (receiving scan loop)
  const [rapidEntryOpen, setRapidEntryOpen] = useState(false);
  const [rapidEntryConfig, setRapidEntryConfig] = useState(null);
  const [rapidEntryRowData, setRapidEntryRowData] = useState(null);

  // Picker overlay state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerConfig, setPickerConfig] = useState(null);
  const [pickerParentRow, setPickerParentRow] = useState(null);

  // Criteria builder overlay state
  const [criteriaBuilderOpen, setCriteriaBuilderOpen] = useState(false);
  const [criteriaBuilderConfig, setCriteriaBuilderConfig] = useState(null);
  const [criteriaBuilderRow, setCriteriaBuilderRow] = useState(null);

  // Count entry overlay state
  const [countEntryOpen, setCountEntryOpen] = useState(false);
  // Add or change a custom field (FH-205): { field } changes that row's field, {} adds one
  const [customFieldDialog, setCustomFieldDialog] = useState(null);
  const [photosDialog, setPhotosDialog] = useState(null); // { entity, entityId, label }
  const [wizardRunDialog, setWizardRunDialog] = useState(null); // { startScript, payload } or { run } (FH-364)
  const [countEntryConfig, setCountEntryConfig] = useState(null);
  const [countEntryRow, setCountEntryRow] = useState(null);

  // Bulk confirm overlay state
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkConfirmRows, setBulkConfirmRows] = useState([]);
  const [bulkConfirmConfig, setBulkConfirmConfig] = useState(null);
  const [bulkConfirmScript, setBulkConfirmScript] = useState(null);

  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);

  // Execute action via FLEX script
  const executeAction = useCallback(async (action, rowData, extraContext) => {
    if (!action.scriptName) {
      console.error(`Action "${action.actionId}" has no script configured.`);
      if (onError) onError(`No FLEX script configured for action "${action.label}".`);
      return;
    }
    try {
      const context = {};
      const payload = { ...(rowData || {}) };
      if (extraContext) {
        Object.assign(payload, extraContext);
        Object.assign(context, extraContext);
      }
      context._payload = payload;

      const response = await FlexService.executeScript({ scriptName: action.scriptName, context });
      // response.success is ENGINE-level only (did the script run without throwing) --
      // most WMS-write scripts report real business-logic failures (e.g. a WMS 400
      // rejection) via their own success/error_message convention without ever
      // throwing, so checking response.success alone missed those entirely and ran
      // onSuccess as if the action worked (FH-56, 2026-10-05 -- found via the same
      // root cause as EntityWizardOverlay.js's save handler).
      const scriptSuccess = response?.context?.success;
      if (!response || !response.success || scriptSuccess === false) {
        const errorMsg = response?.context?.error_message || response?.errorMessage || `Action "${action.label}" failed`;
        console.error(`Action ${action.actionId} failed:`, errorMsg);
        if (onError) onError(errorMsg);
        // a refused action can still have recorded something on the row (e.g. Test Connection's "Failing")
        loadMasterData();
        return;
      }
      if (action.entityFieldsChanged && rowData?.entityName) {
        window.dispatchEvent(new CustomEvent(ENTITY_FIELDS_CHANGED, { detail: { entityName: rowData.entityName } }));
      }
      // Show result to the user
      let result = response?.context?.result;
      // Handle JSONB from DB — PGobject comes as {type:'jsonb', value:'...'} or raw string
      if (result && result.type === 'jsonb' && typeof result.value === 'string') {
        try { result = JSON.parse(result.value); } catch (e) { result = result.value; }
      } else if (typeof result === 'string' && result.startsWith('{')) {
        try { result = JSON.parse(result); } catch (e) { /* keep as string */ }
      }
      const summary = result?.summary || result?.impactSummary || result?.content || result?.message;
      if (result && typeof result === 'object' && Object.keys(result).length > 1 && onSuccess) {
        // Rich result object — show in dialog
        onSuccess(summary || `${action.label || 'Action'} completed.`, result);
      } else if (summary && typeof summary === 'string' && summary.length > 20 && onSuccess) {
        onSuccess(summary, result);
      } else if (onSuccess) {
        onSuccess(`${action.label || 'Action'} completed successfully.`);
      }
      loadMasterData();
    } catch (err) {
      console.error(`Action ${action.actionId} failed:`, err);
      if (onError) onError(err.message);
    }
  }, [loadMasterData, onError]);

  // Form overlay helpers
  const openForm = useCallback((entity, detailEntity, fKey, rowData, opts) => {
    setFormEntity(entity);
    setFormDetailEntity(detailEntity || null);
    setFormKey(fKey || null);
    setFormRowData(rowData || null);
    setFormScriptName(opts?.scriptName || null);
    setFormTitle(opts?.title || null);
    setFormSaveLabel(opts?.saveLabel || null);
    setFormForceCreate(!!opts?.forceCreate);
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setFormEntity(null);
    setFormDetailEntity(null);
    setFormKey(null);
    setFormRowData(null);
    setFormScriptName(null);
    setFormTitle(null);
    setFormSaveLabel(null);
    setFormForceCreate(false);
  }, []);

  // Rapid-entry overlay helpers
  const openRapidEntry = useCallback((rapidEntryCfg, rowData) => {
    setRapidEntryConfig(rapidEntryCfg);
    setRapidEntryRowData(rowData || null);
    setRapidEntryOpen(true);
  }, []);

  const closeRapidEntry = useCallback(() => {
    setRapidEntryOpen(false);
    setRapidEntryConfig(null);
    setRapidEntryRowData(null);
    loadMasterData();
  }, [loadMasterData]);

  // A form that saved says so (Adjust, Change Status, Delete... used to close
  // silently, so the user couldn't tell the save had happened): the Command's own
  // short message if it gives one, otherwise "<form title> completed successfully."
  const handleFormSave = useCallback((result) => {
    const message = savedMessage(result, `${formTitle || 'Changes'} completed successfully.`);
    closeForm();
    loadMasterData();
    if (onSuccess) onSuccess(message);
  }, [closeForm, loadMasterData, onSuccess, formTitle]);

  // Confirm dialog
  const handleConfirm = useCallback(() => {
    if (confirmAction && confirmRow) executeAction(confirmAction, confirmRow);
    setConfirmOpen(false);
    setConfirmAction(null);
    setConfirmRow(null);
  }, [confirmAction, confirmRow, executeAction]);

  const handleConfirmCancel = useCallback(() => {
    setConfirmOpen(false);
    setConfirmAction(null);
    setConfirmRow(null);
  }, []);

  // Edit overlay
  const handleEditSave = useCallback((changedFields) => {
    if (overlayAction) executeAction(overlayAction, overlayRowData, changedFields);
    setOverlayMode(null);
    setOverlayEntity(null);
    setOverlayRowData(null);
    setOverlayAction(null);
  }, [overlayAction, overlayRowData, executeAction]);

  const handleOverlayClose = useCallback(() => {
    setOverlayMode(null);
    setOverlayEntity(null);
    setOverlayRowData(null);
    setOverlayAction(null);
  }, []);

  // Picker
  const closePicker = useCallback(() => {
    setPickerOpen(false);
    setPickerConfig(null);
    setPickerParentRow(null);
  }, []);

  const handlePickerSubmit = useCallback(() => {
    closePicker();
    loadMasterData();
  }, [closePicker, loadMasterData]);

  // Criteria builder
  const closeCriteriaBuilder = useCallback(() => {
    setCriteriaBuilderOpen(false);
    setCriteriaBuilderConfig(null);
    setCriteriaBuilderRow(null);
  }, []);

  const handleCriteriaBuilderSave = useCallback((result) => {
    closeCriteriaBuilder();
    loadMasterData();
    if (onSuccess) onSuccess(savedMessage(result, 'Zone saved.'));
  }, [closeCriteriaBuilder, loadMasterData, onSuccess]);

  // Count entry
  const closeCountEntry = useCallback(() => {
    setCountEntryOpen(false);
    setCountEntryConfig(null);
    setCountEntryRow(null);
  }, []);

  // a submitted count says so: the count screen closes and the task leaves Counting
  const handleCountEntrySave = useCallback((result) => {
    closeCountEntry();
    loadMasterData();
    if (onSuccess) onSuccess(savedMessage(result, 'Count submitted for review.'));
  }, [closeCountEntry, loadMasterData, onSuccess]);

  // Bulk confirm
  const closeBulkConfirm = useCallback(() => {
    setBulkConfirmOpen(false);
    setBulkConfirmRows([]);
    setBulkConfirmConfig(null);
    setBulkConfirmScript(null);
  }, []);

  const handleBulkConfirmComplete = useCallback((_result, opts) => {
    // opts?.keepOpen -- set when the script reported partial/total business-logic
    // failures (result.success === false) so the overlay's own error box stays
    // visible instead of closing as if nothing went wrong. See BulkConfirmOverlay's
    // handleConfirm. The underlying grid still refreshes either way, since any rows
    // that DID succeed should drop out of e.g. the unplanned-orders queue right away.
    if (!opts?.keepOpen) {
      closeBulkConfirm();
      // say what was done, like every other action (FH-242: "Started 2 restock(s).")
      if (onSuccess) onSuccess(savedMessage(_result, 'Done.'));
    }
    loadMasterData();
  }, [closeBulkConfirm, loadMasterData, onSuccess]);

  // Closing an overlay with its X / Cancel also refreshes the grid in place. Several overlays
  // save as they go (counts, scanned lines, a header saved before its lines), so "closed without
  // pressing Save" doesn't mean nothing changed -- the grid used to keep showing the old row.
  const dismissForm = useCallback(() => { closeForm(); loadMasterData(); }, [closeForm, loadMasterData]);
  const dismissOverlay = useCallback(() => { handleOverlayClose(); loadMasterData(); }, [handleOverlayClose, loadMasterData]);
  const dismissPicker = useCallback(() => { closePicker(); loadMasterData(); }, [closePicker, loadMasterData]);
  const dismissCriteriaBuilder = useCallback(() => { closeCriteriaBuilder(); loadMasterData(); }, [closeCriteriaBuilder, loadMasterData]);
  const dismissCountEntry = useCallback(() => { closeCountEntry(); loadMasterData(); }, [closeCountEntry, loadMasterData]);
  const dismissBulkConfirm = useCallback(() => { closeBulkConfirm(); loadMasterData(); }, [closeBulkConfirm, loadMasterData]);

  // Build action handler context — all the state setters and helpers that action handlers need
  const actionContext = useMemo(() => ({
    // Navigation
    setActiveViewId, pendingNavParamsRef,
    // Cross-workspace navigation (NAVIGATE with targetWorkspace) -- opens a
    // different workspace entirely as a new/focused tab, as opposed to
    // setActiveViewId which only switches tabs within THIS workspace.
    onOpenWorkspace,
    // Overlays
    setOverlayMode, setOverlayEntity, setOverlayRowData, setOverlayAction,
    // Form
    openForm,
    // Rapid-entry (receiving scan loop)
    openRapidEntry,
    // Picker
    setPickerConfig, setPickerOpen, setPickerParentRow,
    // Criteria builder
    setCriteriaBuilderConfig, setCriteriaBuilderOpen, setCriteriaBuilderRow,
    // Count entry
    setCountEntryConfig, setCountEntryOpen, setCountEntryRow,
    // Bulk confirm
    setBulkConfirmConfig, setBulkConfirmOpen, setBulkConfirmRows, setBulkConfirmScript,
    // Confirm dialog
    setConfirmAction, setConfirmRow, setConfirmOpen,
    // Custom field dialog
    setCustomFieldDialog,
    // Photos of a record
    setPhotosDialog,
    // An inspection or work order in the stepped panel
    setWizardRunDialog,
    // Data
    activeView, resolvedDetails, loadMasterData, onError,
    // Current view's NAVIGATE-scoped params (e.g. {zoneId: "Z1"} after
    // drilling into a hidden detail view via contextParams) -- merged into a
    // header CREATE-mode FORM action's initial payload in _handleFormWizard,
    // same idea as parentParams for a detail grid's own load call.
    navParams,
    // Services
    FlexService,
    // Execution
    executeAction,
  }), [activeView, resolvedDetails, loadMasterData, onError, executeAction, openForm, openRapidEntry,
       setActiveViewId, pendingNavParamsRef, navParams, onOpenWorkspace]);

  // Handle row action from grid — delegates to action handler registry
  const handleRowAction = useCallback((action, row) => {
    const actionType = action.actionType || action.type;
    const rowData = row.data || row;

    // Try registry first
    const handler = actionHandlerRegistry.resolve(actionType);
    if (handler) {
      handler(action, rowData, actionContext);
      return;
    }

    // Fallback: confirm dialog or direct execution
    if (action.confirmMessage || action.confirm) {
      const msg = typeof action.confirmMessage === 'string' ? action.confirmMessage : 'Are you sure you want to delete this record?';
      const resolvedMessage = msg.replace(/\{\{(\w+)\}\}/g, (_, key) => rowData[key] ?? '');
      setConfirmAction({ ...action, confirmMessage: resolvedMessage });
      setConfirmRow(rowData);
      setConfirmOpen(true);
    } else {
      executeAction(action, rowData);
    }
  }, [actionContext, executeAction]);

  // Handle global action (header buttons) — delegates to action handler registry.
  // Optional `contextRow` is passed by detail grids (AccordionDetail) so detail-header
  // actions can access the parent (master) row as their rowData context. Master-grid
  // global actions pass no row context (contextRow is undefined → action handler gets null).
  const handleGlobalAction = useCallback(async (action, contextRow) => {
    const aType = action.actionType || action.type;

    // an action that can't run right now says why up front, instead of failing after the user fills it in
    if (action.precheckScript) {
      try {
        const resp = await FlexService.executeScript({ scriptName: action.precheckScript, context: {} });
        const check = resp?.context?.result;
        if (check && check.ok === false) {
          if (onError) onError(check.message || 'This can\'t be done right now.');
          return;
        }
      } catch (err) {
        // a check that fails to run never blocks the action
      }
    }

    // REFRESH is special — also matches by actionId
    if (aType === 'REFRESH' || action.actionId === 'REFRESH') {
      loadMasterData();
      return;
    }

    // Try registry
    const handler = actionHandlerRegistry.resolve(aType);
    if (handler) {
      handler(action, contextRow || null, actionContext);
      return;
    }

    // Fallback: direct execution
    executeAction(action, contextRow || null);
  }, [actionContext, loadMasterData, executeAction, onError]);

  const closeCustomFieldDialog = useCallback(() => setCustomFieldDialog(null), []);
  const customFieldSaved = useCallback(() => { setCustomFieldDialog(null); loadMasterData(); }, [loadMasterData]);

  return {
    executeAction,
    // Custom field dialog
    customFieldDialog, closeCustomFieldDialog, customFieldSaved,
    // Photos of a record
    photosDialog, closePhotosDialog: () => setPhotosDialog(null),
    wizardRunDialog, closeWizardRunDialog: () => { setWizardRunDialog(null); loadMasterData(); },
    handleRowAction,
    handleGlobalAction,
    // Edit overlay
    overlayMode, overlayEntity, overlayRowData, handleEditSave, handleOverlayClose, dismissOverlay,
    // Form overlay
    formOpen, formEntity, formDetailEntity, formKey, formRowData, formScriptName, formTitle, formSaveLabel, formForceCreate,
    openForm, closeForm, dismissForm, handleFormSave,
    // Rapid-entry overlay (receiving scan loop)
    rapidEntryOpen, rapidEntryConfig, rapidEntryRowData, openRapidEntry, closeRapidEntry,
    // Picker
    pickerOpen, pickerConfig, pickerParentRow, closePicker, dismissPicker, handlePickerSubmit,
    // Criteria builder
    criteriaBuilderOpen, criteriaBuilderConfig, criteriaBuilderRow, closeCriteriaBuilder, dismissCriteriaBuilder, handleCriteriaBuilderSave,
    // Count entry
    countEntryOpen, countEntryConfig, countEntryRow, closeCountEntry, dismissCountEntry, handleCountEntrySave,
    // Bulk confirm
    bulkConfirmOpen, bulkConfirmRows, bulkConfirmConfig, bulkConfirmScript, closeBulkConfirm, dismissBulkConfirm, handleBulkConfirmComplete,
    // Confirm dialog
    confirmOpen, confirmAction, handleConfirm, handleConfirmCancel,
  };
};

export default useWorkspaceActions;
